import { useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { invalidate } from './cache';

/**
 * Świeża lista zleceń (21.10, uwaga Maćka): zakończone zlecenie ma być od razu na listach (Gotowość, Historia).
 *
 * - `notifyOrdersChanged(detail)` — unieważnia cache list zleceń i daje znać otwartym stronom (zdarzenie okna).
 * - `useOrdersWatch(...)` — JEDEN lekki nasłuch dla całej aplikacji: tylko gdy są zlecenia w toku (processing/queued),
 *   co 10 s jedno małe zapytanie. Nic w toku → zero odpytywania (baza Nano). Bez Realtime (zasada B1).
 * - `kickOrdersWatch()` — „sprawdź teraz” (np. zaraz po uruchomieniu zlecenia).
 */
// `neworder:active:` — migawka zleceń w toku dla „Nowe zlecenie”; unieważniona = ekran czeka na sieć (nie pokazuje starej kolejki).
export const ORDER_LIST_KEYS = ['readiness:orders', 'history:orders:', 'neworder:active:'];
const CHANGED = 'mams:orders-changed';
const KICK = 'mams:orders-watch';
const POLL_MS = 10_000;

export function notifyOrdersChanged(detail = {}) {
  ORDER_LIST_KEYS.forEach(k => invalidate(k));
  try { window.dispatchEvent(new CustomEvent(CHANGED, { detail })); } catch { /* bez window */ }
}

/** Subskrypcja zmian list zleceń; zwraca funkcję odpinającą. `fn(detail)` — detail.finished = [id…], detail.created = [id…]. */
export function onOrdersChanged(fn) {
  const h = (e) => fn(e.detail || {});
  window.addEventListener(CHANGED, h);
  return () => window.removeEventListener(CHANGED, h);
}

/** „Sprawdź teraz”; `ids` — świeżo uruchomione zlecenia (nawet gdy skończą się przed pierwszym odczytem, będzie toast). */
export function kickOrdersWatch(ids = []) {
  try { window.dispatchEvent(new CustomEvent(KICK, { detail: { ids } })); } catch { /* bez window */ }
}

const ACTIVE = ['processing', 'queued'];

/**
 * @param enabled  zalogowany i dane konta wczytane
 * @param userId   gdy podany (`ownOnly`) — tylko zlecenia tego użytkownika (admin widzi wszystkie; bez tego toasty z cudzych zleceń)
 * @param onFinished(orders) — zlecenia, które właśnie przeszły z „w toku” na done/error/cancelled
 */
export function useOrdersWatch({ enabled, userId = null, onFinished }) {
  const known = useRef(new Map()); // id → status (tylko w toku)
  const timer = useRef(null);
  const running = useRef(false);
  const cb = useRef(onFinished);
  cb.current = onFinished;

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    known.current = new Map();

    const schedule = () => {
      clearTimeout(timer.current);
      timer.current = null;
      if (alive && known.current.size > 0) timer.current = setTimeout(check, POLL_MS);
    };

    const check = async () => {
      if (!alive || running.current) return;
      if (document.visibilityState === 'hidden') { schedule(); return; }
      running.current = true;
      try {
        let q = supabase.from('orders').select('id, status').in('status', ACTIVE).limit(50);
        if (userId) q = q.eq('user_id', userId);
        const { data, error } = await q;
        if (error) return;
        const now = new Map((data || []).map(o => [o.id, o.status]));
        const gone = [...known.current.keys()].filter(id => !now.has(id));
        known.current = now;
        if (gone.length) {
          const { data: fin } = await supabase.from('orders').select('id, status, marketplace, language, tenant_id, filename').in('id', gone);
          (fin || []).filter(o => ACTIVE.includes(o.status)).forEach(o => known.current.set(o.id, o.status)); // wyścig z odczytem
          const finished = (fin || []).filter(o => !ACTIVE.includes(o.status));
          if (finished.length) {
            notifyOrdersChanged({ finished: finished.map(o => o.id) });
            cb.current?.(finished);
          }
        }
      } finally {
        running.current = false;
        schedule();
      }
    };

    const kick = (e) => {
      (e?.detail?.ids || []).forEach(id => { if (!known.current.has(id)) known.current.set(id, 'queued'); });
      clearTimeout(timer.current);
      check();
    };
    const onVis = () => { if (document.visibilityState === 'visible') kick(); };
    check(); // jeden odczyt na starcie — jeśli nic nie jest w toku, na tym koniec
    window.addEventListener(KICK, kick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      clearTimeout(timer.current);
      window.removeEventListener(KICK, kick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, userId]);
}
